import { Card, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { SearchGrid } from '@/components/SearchGrid';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { FONT_WEIGHT, SMBold } from '@/typography/Typography';
import { useEffect, useRef, useState } from 'react';
import {
    useGetPOSLocationsAndDepartments,
    useTipsHistory,
    useTipsHistoryExcelDownload
} from '@/services/paymentsQuery';
import CardHeader from '@/components/shared/CardHeader';
import Chip from '@/components/shared/Chip';
import { TipsLocationList } from './TipsLocationList';
import {
    TablePagination,
    usePagination
} from '@/components/shared/TablePagination';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import {
    PrimaryButton,
    SecondaryButtonRegular
} from '@/components/shared/Button';
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';

export const TipsListPage = () => {
    const [locationName, setLocationName] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [reportingStatusFilter, setReportingStatusFilter] =
        useState<string>('all');
    const {
        data,
        isPending: locationPending,
        error: locationError
    } = useGetPOSLocationsAndDepartments();

    useHandleResponse({
        error: locationError
    });
    const { page, resetPage, rowsPerPage, setRowsPerPage } = usePagination();

    const {
        data: tipsHistoryData,
        isPending: historyPending,
        error: historyError
    } = useTipsHistory(
        page * rowsPerPage,
        rowsPerPage,
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter]
    );

    useHandleResponse({
        error: historyError
    });

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLoader(locationPending || historyPending));
    }, [locationPending, historyPending]);

    const {
        data: excelData,
        refetch: refetchExcel,
        error: excelError,
        isSuccess: isExcelSuccess,
        isPending: excelPending
    } = useTipsHistoryExcelDownload(
        locationName === 'all' ? [] : [locationName],
        statusFilter === 'all' ? [] : [statusFilter],
        reportingStatusFilter === 'all' ? [] : [reportingStatusFilter],
        false
    );

    const downloadLock = useRef(true);

    useHandleResponse({
        error: excelError
    });

    useEffect(() => {
        if (isExcelSuccess && !downloadLock.current) {
            downloadLock.current = true; // Prevent multiple downloads
            const sheet = XLSXUtils.aoa_to_sheet([]);
            const headers =
                excelData?.data?.tipsHistory &&
                excelData.data.tipsHistory.length > 0
                    ? Object.keys(excelData.data.tipsHistory[0])
                    : [];
            // Add headers with bold style

            XLSXUtils.sheet_add_aoa(sheet, [headers], { origin: 'A1' });
            headers.forEach((header, idx) => {
                const cellAddress = XLSXUtils.encode_cell({ r: 0, c: idx });
                if (!sheet[cellAddress])
                    sheet[cellAddress] = { t: 's', v: header };
                sheet[cellAddress].s = {
                    font: { bold: true }
                };
            });
            if (excelData?.data?.tipsHistory) {
                const rows = excelData.data.tipsHistory.map((tip) =>
                    Object.values(tip).map((value) =>
                        value &&
                        typeof value === 'object' &&
                        typeof value.format === 'function'
                            ? value.toString()
                            : value
                    )
                );

                XLSXUtils.sheet_add_aoa(sheet, rows, { origin: 'A2' });
            }
            const workbook = XLSXUtils.book_new();
            XLSXUtils.book_append_sheet(workbook, sheet, 'Tips History');
            const fileName = `Tips_History_${
                new Date().toISOString().split('T')[0]
            }.xlsx`;
            XLSXWriteFile(workbook, fileName, {
                bookType: 'xlsx',
                type: 'binary'
            });
        }
    }, [excelData, isExcelSuccess]);

    const resetFilters = () => {
        setLocationName('all');
        setStatusFilter('all');
        setReportingStatusFilter('all');
        resetPage();
    };

    const actions = (
        <Stack direction="row" gap={'8px'}>
            <SecondaryButtonRegular
                onClick={() => {
                    downloadLock.current = false;
                    refetchExcel();
                }}
                size="small"
                sx={{ borderRadius: '5px' }}
            >
                {t('download_as_excel')}
            </SecondaryButtonRegular>
        </Stack>
    );

    return (
        <div>
            <SearchGrid container justifyContent={'space-between'}>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    gap={1}
                    justifyContent={'space-between'}
                >
                    <div>
                        <SMBold noWrap={true}>{t('filter_by')}</SMBold>
                    </div>
                    <Dropdown
                        style={{ width: '400px' }}
                        choices={[
                            {
                                label: t('tips.show_all_locations'),
                                value: 'all'
                            },
                            ...(data?.data?.locations?.map((filter) => ({
                                label: filter.locationName,
                                value: filter.locationId
                            })) || [])
                        ]}
                        label=""
                        value={locationName}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            resetPage();
                            setLocationName(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.status_awaiting'),
                                value: 'Awaiting'
                            },
                            {
                                label: t('tips.status_succeeded'),
                                value: 'Succeeded'
                            }
                        ]}
                        label=""
                        value={statusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            resetPage();
                            setStatusFilter(val);
                        }}
                    />
                    <Dropdown
                        style={{ width: '250px' }}
                        choices={[
                            {
                                label: t('tips.show_all_reporting_statuses'),
                                value: 'all'
                            },
                            {
                                label: t('tips.reporting_status_not_reported'),
                                value: 'NotReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_partially_reported'
                                ),
                                value: 'PartiallyReported'
                            },
                            {
                                label: t(
                                    'tips.reporting_status_fully_reported'
                                ),
                                value: 'FullyReported'
                            }
                        ]}
                        label=""
                        value={reportingStatusFilter}
                        type={DropdownType.SingleShowTitle}
                        onChange={(val) => {
                            resetPage();
                            setReportingStatusFilter(val);
                        }}
                    />
                    <PrimaryButton onClick={resetFilters}>
                        <Typography
                            fontSize={'14px'}
                            lineHeight={'20px'}
                            fontWeight={600}
                            noWrap
                        >
                            {t('reset_filter')}
                        </Typography>
                    </PrimaryButton>
                </Stack>
            </SearchGrid>

            <Card>
                <CardHeader
                    label={t('tips.daily_settlements')}
                    chip={
                        <Chip
                            icon={<span></span>}
                            label={
                                <Typography fontWeight={FONT_WEIGHT.SEMI_BOLD}>
                                    {t('filtered_of_total', {
                                        filtered:
                                            tipsHistoryData?.data
                                                ?.filteredTotal ?? 0,
                                        total: tipsHistoryData?.data?.total ?? 0
                                    })}
                                </Typography>
                            }
                            color={'success'}
                        />
                    }
                    actions={actions}
                />
                <TipsLocationList
                    items={tipsHistoryData?.data?.tipsHistory}
                    reloadTransactions={() => {}}
                ></TipsLocationList>
                {(tipsHistoryData?.data?.tipsHistory?.length ?? 0) == 0 && (
                    <Typography textAlign={'center'}>
                        {t('no_records')}
                    </Typography>
                )}
                <TablePagination
                    page={page}
                    rowsPerPageOptions={[
                        {
                            label: '16',
                            value: 16
                        },
                        {
                            label: '24',
                            value: 24
                        },
                        {
                            label: '32',
                            value: 32
                        },
                        {
                            label: 'All',
                            value: -1
                        }
                    ]}
                    count={tipsHistoryData?.data?.total ?? 0}
                    rowsPerPage={rowsPerPage}
                    resetPage={resetPage}
                    onPageChange={() => {}}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage as number);
                    }}
                />
            </Card>
        </div>
    );
};
